import { useMemo } from "react";
import dayjs from "dayjs";
import { Tag } from "antd";
import { Table, Button, type TableColumn } from "@libertify/ui";
import { useTranslate, useTranslation } from "@refinedev/core";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { formatAmount } from "../helpers";
import { InvoiceResponse, InvoiceStatus } from "@/types";

interface BillingHistoryTableProps {
  invoices?: InvoiceResponse[];
  isLoading?: boolean;
}

const statusColor: Record<InvoiceStatus, string> = {
  paid: "success",
  open: "processing",
  draft: "default",
  void: "default",
  uncollectible: "error",
};

export const BillingHistoryTable = ({
  invoices,
  isLoading,
}: BillingHistoryTableProps) => {
  const t = useTranslate();
  const { getLocale } = useTranslation();

  const columns = useMemo<TableColumn<InvoiceResponse>[]>(
    () => [
      {
        title: t("pages.subscription.billingHistory.columns.date"),
        dataIndex: "created",
        align: "center",
        render: (value) => dayjs(value as string).format("MMM DD, YYYY"),
      },
      {
        title: t("pages.subscription.billingHistory.columns.amount"),
        key: "amount",
        align: "center",
        render: (_value, record) =>
          formatAmount(record.amount, record.currency, getLocale()),
      },
      {
        title: t("pages.subscription.billingHistory.columns.status"),
        dataIndex: "status",
        align: "center",
        render: (value) => (
          <Tag color={statusColor[value as InvoiceStatus]}>
            {t(`pages.subscription.billingHistory.statuses.${value}`)}
          </Tag>
        ),
      },
      {
        title: t("pages.subscription.billingHistory.columns.invoice"),
        key: "invoice_pdf",
        align: "center",
        render: (_value, record) => (
          <Button
            variant="tertiary"
            size="s"
            leftIcon={<DownloadSimpleIcon size={16} />}
            href={record.invoice_pdf ?? undefined}
            target="_blank"
            disabled={!record.invoice_pdf}
          >
            {t("pages.subscription.billingHistory.download")}
          </Button>
        ),
      },
    ],
    [t, getLocale]
  );

  return (
    <Table<InvoiceResponse>
      data={invoices ?? []}
      columns={columns}
      loading={isLoading}
      rowKey="id"
      pagination={{ pageSize: 20, hideOnSinglePage: true }}
    />
  );
};

export default BillingHistoryTable;
